import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Country, State, City } from 'country-state-city';
import apiService from '../services/apiService';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const FormInput = ({ label, name, type, value, onChange, required, placeholder, accept, multiple, className }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>
    <input
      type={type}
      name={name}
      value={type !== 'file' ? value : undefined}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      accept={accept}
      multiple={multiple}
      className={`w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary transition duration-200 ${className || ''}`}
    />
  </div>
);

const Signup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    password: '',
    address: '',
    phone: '',
    postalCode: '',
    street: '',
    country: '',
    state: '',
    city: '',
    active: true,
    isVerified: false,
    documentTypes: [],
    documents: [],
    profilePicture: null,
    fees: 0,
    availability: '',
    bio: '',
    specialties: [],
    experience: 0,
    userType: 'individual',
    companyName: '',
  });

  const [steps, setSteps] = useState([
    { title: 'Personal Information', icon: '👤' },
    { title: 'Contact Information', icon: '📍' },
    { title: 'Professional Details', icon: '💼', hidden: form.role !== 'specialist' },
    { title: 'Documents', icon: '📄', hidden: form.role !== 'specialist' },
  ]);

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file' && name === 'documents') {
      const newDocuments = Array.from(files);
      setForm({
        ...form,
        documents: newDocuments,
        documentTypes: newDocuments.map(() => 'certificate'),
      });
    } else {
      setForm({
        ...form,
        [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value,
      });
    }
  };

  const handleDocumentTypeChange = (index, selected) => {
    const newDocumentTypes = [...form.documentTypes];
    newDocumentTypes[index] = selected ? selected.value : 'certificate';
    setForm({ ...form, documentTypes: newDocumentTypes });
  };

  const handlePhoneChange = (value) => {
    setForm({ ...form, phone: value });
  };

  const handleRoleChange = (selected) => {
    const newRole = selected ? selected.value : '';
    setForm({
      ...form,
      role: newRole,
      userType: 'individual',
      companyName: '',
      ...(!['specialist'].includes(newRole) && {
        fees: 0,
        availability: '',
        bio: '',
        specialties: [],
        experience: 0,
        documents: [],
        documentTypes: [],
      }),
    });
    setSteps([
      { title: 'Personal Information', icon: '👤' },
      { title: 'Contact Information', icon: '📍' },
      { title: 'Professional Details', icon: '💼', hidden: newRole !== 'specialist' },
      { title: 'Documents', icon: '📄', hidden: newRole !== 'specialist' },
    ]);
  };

  const handleUserTypeChange = (selected) => {
    const newType = selected ? selected.value : 'individual';
    setForm({
      ...form,
      userType: newType,
      ...(newType === 'company' && { firstName: '', lastName: '' }),
      ...(newType === 'individual' && { companyName: '' }),
    });
  };

  const handleCountryChange = (selected) => {
    const newCountry = selected ? selected.value : '';
    setForm({ ...form, country: newCountry, state: '', city: '' });
  };

  const handleStateChange = (selected) => {
    const newState = selected ? selected.value : '';
    setForm({ ...form, state: newState, city: '' });
  };

  const handleCityChange = (selected) => {
    setForm({ ...form, city: selected ? selected.value : '' });
  };

  const handleAvailabilityChange = (selected) => {
    setForm({ ...form, availability: selected ? selected.value : '' });
  };

  const handleSpecialtyChange = (selected) => {
    if (selected.length > 5) {
      toast.error('Maximum 5 specialties allowed.');
      return;
    }
    setForm({ ...form, specialties: selected.map((opt) => opt.value) });
  };

  const validateStep = () => {
    if (step === 1) {
      if ((form.role === 'user' || form.role === 'waste_collector') && form.userType === 'company') {
        return form.companyName?.trim() && form.email && form.role && form.password;
      }
      return form.firstName?.trim() && form.lastName?.trim() && form.email && form.role && form.password;
    }
    if (step === 2) {
      return form.country && form.state && form.address && form.street && form.postalCode && form.phone;
    }
    if (step === 3 && form.role === 'specialist') {
      return form.fees >= 0 && form.experience >= 0 && form.specialties.length > 0;
    }
    if (step === 4 && form.role === 'specialist') {
      return (
        form.documents.length > 0 &&
        form.documentTypes.length === form.documents.length &&
        form.documentTypes.every((type) => ['certificate', 'license', 'farm_ownership'].includes(type))
      );
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (form.role !== 'specialist' && step === 2) {
        handleSubmit({ preventDefault: () => {} });
      } else if (step === 3 && form.role === 'specialist') {
        setStep(4);
      } else {
        setStep(step + 1);
      }
    } else {
      toast.error('Please fill all required fields.');
    }
  };

  const handleBack = () => {
    if (step > 1) {
      if (step === 4 && form.role === 'specialist') {
        setStep(3);
      } else {
        setStep(step - 1);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) {
      toast.error('Please fill all required fields.');
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();

      // Prepare name fields based on userType
      const submitData = { ...form };
      if ((form.role === 'user' || form.role === 'waste_collector') && form.userType === 'company') {
        submitData.firstName = form.companyName || '';
        submitData.lastName = '';
      }

      Object.entries(submitData).forEach(([key, value]) => {
        if (key === 'specialties' && Array.isArray(value)) {
          value.forEach((specialty, index) => formData.append(`specialties[${index}]`, specialty));
        } else if (key === 'documents' && Array.isArray(value) && value.length > 0) {
          value.forEach((doc) => formData.append('documents', doc));
          form.documentTypes.forEach((type, index) => formData.append(`documentTypes[${index}]`, type));
        } else if (key === 'documentTypes') {
          // skipped - already appended above
        } else if (key === 'profilePicture' && value) {
          formData.append('profilePicture', value);
        } else if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      await apiService.upload('/users', formData, { method: 'POST' });

      localStorage.setItem('signupEmail', form.email);
      toast.success('Account created successfully! Please check your email to verify your account.');

      setForm({
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        password: '',
        address: '',
        phone: '',
        postalCode: '',
        street: '',
        country: '',
        state: '',
        city: '',
        active: true,
        isVerified: false,
        documentTypes: [],
        documents: [],
        profilePicture: null,
        fees: 0,
        availability: '',
        bio: '',
        specialties: [],
        experience: 0,
        userType: 'individual',
        companyName: '',
      });

      setSteps([...steps, { title: 'Thank You', icon: '✅' }]);
      setStep(5);
    } catch (err) {
      toast.error(err.message || 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setIsResending(true);
    try {
      await apiService.post('/auth/resend-verification', { email: localStorage.getItem('signupEmail') });
      toast.success('Verification email resent successfully! Please check your email.');
    } catch (err) {
      toast.error(err.message || 'Failed to resend verification email.');
    } finally {
      setIsResending(false);
    }
  };

  const countryOptions = Country.getAllCountries().map((c) => ({
    value: c.isoCode,
    label: c.name,
  }));

  const stateOptions = form.country
    ? State.getStatesOfCountry(form.country).map((s) => ({
        value: s.isoCode,
        label: s.name,
      }))
    : [];

  const cityOptions = form.country && form.state
    ? City.getCitiesOfState(form.country, form.state).map((c) => ({
        value: c.name,
        label: c.name,
      }))
    : [];

  const availabilityOptions = [
    { value: 'available', label: 'Available' },
    { value: 'not_available', label: 'Not Available' },
  ];

  const specialtyOptions = [
    { value: 'poultry_care', label: 'Poultry Care' },
    { value: 'cattle_health', label: 'Cattle Health' },
    { value: 'veterinary_surgery', label: 'Veterinary Surgery' },
    { value: 'animal_nutrition', label: 'Animal Nutrition' },
    { value: 'livestock_management', label: 'Livestock Management' },
    { value: 'disease_control', label: 'Disease Control' },
    { value: 'farm_vaccination', label: 'Farm Vaccination' },
    { value: 'poultry_diagnostics', label: 'Poultry Diagnostics' },
    { value: 'avian_diseases', label: 'Avian Diseases Specialist' },
    { value: 'hatchery_management', label: 'Hatchery Management' },
    { value: 'broiler_management', label: 'Broiler Management' },
    { value: 'layer_management', label: 'Layer Farm Management' },
    { value: 'biosecurity_management', label: 'Biosecurity Management' },
    { value: 'feed_formulation', label: 'Feed Formulation' },
    { value: 'parasite_control', label: 'Parasite Control' },
    { value: 'antibiotic_stewardship', label: 'Antibiotic Stewardship' },
    { value: 'reproductive_health', label: 'Reproductive Health' },
    { value: 'egg_quality_management', label: 'Egg Quality Management' },
    { value: 'farm_sanitation', label: 'Farm Sanitation & Hygiene' },
    { value: 'environmental_management', label: 'Poultry Environment Management' },
    { value: 'heat_stress_management', label: 'Heat Stress Management' },
    { value: 'poultry_welfare', label: 'Animal Welfare (Poultry)' },
    { value: 'veterinary_consultation', label: 'General Veterinary Consultation' },
  ];

  const documentTypeOptions = [
    { value: 'certificate', label: 'Certificate' },
    { value: 'license', label: 'License' },
    { value: 'farm_ownership', label: 'Farm Ownership' },
  ];

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderColor: 'secondary',
      '&:hover': { borderColor: 'secondary' },
      boxShadow: 'none',
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      padding: '0.25rem',
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.5rem',
      border: '1px solid secondary',
      backgroundColor: '#ffffff',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'secondary' : state.isFocused ? '#f9e8d9' : '#ffffff',
      color: state.isSelected ? '#ffffff' : '#1f2937',
      '&:hover': { backgroundColor: '#f9e8d9' },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#1f2937',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#f9e8d9',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#1f2937',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#1f2937',
      '&:hover': {
        backgroundColor: 'secondary',
        color: '#ffffff',
      },
    }),
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 sm:p-6">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 sm:p-8">
        {/* Progress Bar */}
        <div className="mb-10">
          <div className="flex justify-between items-center">
            {steps.map((s, index) => (
              !s.hidden && (
                <div key={index} className="flex-1 text-center">
                  <div
                    className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center text-lg font-semibold ${
                      step > index + 1
                        ? 'bg-secondary text-white'
                        : step === index + 1
                        ? 'bg-primary text-white'
                        : 'bg-gray-200 text-gray-600'
                    } transition-colors duration-300`}
                  >
                    {s.icon}
                  </div>
                  <p className="mt-2 text-sm font-medium text-gray-700">{s.title}</p>
                </div>
              )
            ))}
          </div>
          <div className="mt-4 h-2 bg-gray-200 rounded-full">
            <div
              className="h-full bg-secondary rounded-full transition-all duration-300"
              style={{
                width: `${((step - 1) / (steps.filter((s) => !s.hidden).length - 1)) * 100}%`,
              }}
            ></div>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {step === 1 && (
            <div>
              <h2 className="text-3xl font-extrabold text-primary mb-6">Create Your Account</h2>
              <p className="text-sm text-gray-500 mb-6">Enter your personal details to get started.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormInput
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter email"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                  <Select
                    options={[
                      { value: 'farmer', label: 'Farmer' },
                      { value: 'specialist', label: 'Specialist' },
                      { value: 'waste_collector', label: 'Waste Collector' },
                      { value: 'user', label: 'User' },
                    ]}
                    value={[{ value: form.role, label: form.role }].filter((opt) => opt.value)}
                    onChange={handleRoleChange}
                    placeholder="Select a role"
                    isClearable
                    styles={customSelectStyles}
                    className="w-full"
                    classNamePrefix="react-select"
                    required
                  />
                </div>

                {(form.role === 'user' || form.role === 'waste_collector') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Account Type</label>
                    <Select
                      options={[
                        { value: 'individual', label: 'Individual' },
                        { value: 'company', label: 'Company' },
                      ]}
                      value={
                        form.userType
                          ? {
                              value: form.userType,
                              label: form.userType === 'company' ? 'Company' : 'Individual',
                            }
                          : null
                      }
                      onChange={handleUserTypeChange}
                      placeholder="Select account type"
                      styles={customSelectStyles}
                      className="w-full"
                      classNamePrefix="react-select"
                    />
                  </div>
                )}

                {(form.role === 'user' || form.role === 'waste_collector') && form.userType === 'company' ? (
                  <FormInput
                    label="Company Name"
                    name="companyName"
                    type="text"
                    value={form.companyName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter company name"
                    className="md:col-span-2"
                  />
                ) : (
                  <>
                    <FormInput label="First Name" name="firstName" type="text" value={form.firstName} onChange={handleInputChange} required />
                    <FormInput label="Last Name" name="lastName" type="text" value={form.lastName} onChange={handleInputChange} required />
                  </>
                )}

                <FormInput label="Password" name="password" type="password" value={form.password} onChange={handleInputChange} required />
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="text-3xl font-extrabold text-primary mb-6">Contact Information</h2>
              <p className="text-sm text-gray-500 mb-6">Provide your contact details for account setup.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                  <Select
                    options={countryOptions}
                    value={countryOptions.find((opt) => opt.value === form.country) || null}
                    onChange={handleCountryChange}
                    placeholder="Select a country"
                    isClearable
                    styles={customSelectStyles}
                    className="w-full"
                    classNamePrefix="react-select"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <Select
                    options={stateOptions}
                    value={stateOptions.find((opt) => opt.value === form.state) || null}
                    onChange={handleStateChange}
                    placeholder="Select a state"
                    isClearable
                    isDisabled={!form.country}
                    styles={customSelectStyles}
                    className="w-full"
                    classNamePrefix="react-select"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <Select
                    options={cityOptions}
                    value={cityOptions.find((opt) => opt.value === form.city) || null}
                    onChange={handleCityChange}
                    placeholder="Select a city (optional)"
                    isClearable
                    isDisabled={!form.state}
                    styles={customSelectStyles}
                    className="w-full"
                    classNamePrefix="react-select"
                  />
                </div>
                <FormInput
                  label="Address"
                  name="address"
                  type="text"
                  value={form.address}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter address"
                />
                <FormInput
                  label="Street"
                  name="street"
                  type="text"
                  value={form.street}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter street"
                />
                <FormInput
                  label="Postal Code"
                  name="postalCode"
                  type="text"
                  value={form.postalCode}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter postal code"
                />
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <PhoneInput
                    country={'us'}
                    value={form.phone}
                    onChange={handlePhoneChange}
                    placeholder="Enter phone number"
                    inputClass="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary transition duration-200"
                    dropdownClass="border border-gray-300 dark:border-gray-600 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <FormInput
                    label="Profile Picture (JPG/PNG)"
                    name="profilePicture"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && form.role === 'specialist' && (
            <div>
              <h2 className="text-3xl font-extrabold text-primary mb-6">Professional Details</h2>
              <p className="text-sm text-gray-500 mb-6">Share your professional background and expertise.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormInput
                  label="Fees (£)"
                  name="fees"
                  type="number"
                  value={form.fees}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter fees"
                  min="0"
                  step="0.01"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Availability</label>
                  <Select
                    options={availabilityOptions}
                    value={availabilityOptions.find((opt) => opt.value === form.availability) || null}
                    onChange={handleAvailabilityChange}
                    placeholder="Select availability"
                    isClearable
                    styles={customSelectStyles}
                    className="w-full"
                    classNamePrefix="react-select"
                  />
                </div>
                <FormInput
                  label="Experience (Years)"
                  name="experience"
                  type="number"
                  value={form.experience}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter years of experience"
                  min="0"
                  max="50"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Specialties</label>
                  <Select
                    options={specialtyOptions}
                    value={specialtyOptions.filter((opt) => form.specialties.includes(opt.value))}
                    onChange={handleSpecialtyChange}
                    isMulti
                    placeholder="Select up to 5 specialties"
                    isClearable
                    styles={customSelectStyles}
                    className="w-full"
                    classNamePrefix="react-select"
                    maxMenuHeight={200}
                  />
                  {form.specialties.length > 0 && (
                    <p className="text-sm text-gray-500 mt-2">Selected: {form.specialties.length} / 5</p>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                  <textarea
                    name="bio"
                    value={form.bio}
                    onChange={handleInputChange}
                    placeholder="Enter bio or about yourself"
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary transition duration-200"
                    rows="5"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 4 && form.role === 'specialist' && (
            <div>
              <h2 className="text-3xl font-extrabold text-primary mb-6">Documents and Verification</h2>
              <p className="text-sm text-gray-500 mb-6">Upload documents to verify your specialist credentials.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <FormInput
                    label="Upload Documents (PDF/JPG/PNG)"
                    name="documents"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleInputChange}
                    multiple
                  />
                </div>
                {form.documents.length > 0 && (
                  <div className="col-span-2">
                    <h3 className="text-lg font-medium text-gray-700 mb-4">Document Types</h3>
                    {form.documents.map((doc, index) => (
                      <div key={index} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Document {index + 1}: {doc.name}
                        </label>
                        <Select
                          options={documentTypeOptions}
                          value={documentTypeOptions.find((opt) => opt.value === form.documentTypes[index]) || null}
                          onChange={(selected) => handleDocumentTypeChange(index, selected)}
                          placeholder="Select document type"
                          isClearable
                          styles={customSelectStyles}
                          className="w-full"
                          classNamePrefix="react-select"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="text-center">
              <h2 className="text-3xl font-extrabold text-primary mb-6">Thank You for Signing Up!</h2>
              <p className="text-base text-gray-600 mb-6 max-w-md mx-auto">
                Your account has been created successfully. Please check your email at{' '}
                <span className="font-medium">{form.email}</span> to verify your account. The verification link has been sent to your inbox (check your spam/junk folder if you don't see it).
              </p>
              <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => navigate('/login')}
                  className="px-6 py-2 bg-secondary text-white font-semibold rounded-md hover:bg-opacity-90 transition duration-300"
                >
                  Back to Login
                </button>
                <button
                  onClick={handleResendEmail}
                  disabled={isResending}
                  className="px-6 py-2 bg-transparent border-2 border-secondary text-secondary font-semibold rounded-md hover:bg-secondary hover:text-white transition duration-300 disabled:opacity-70"
                >
                  {isResending ? 'Resending...' : 'Resend Email'}
                </button>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          {step < 5 && (
            <div className="flex justify-between mt-8">
              <div>
                {step > 1 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="px-6 py-2 bg-gray-300 text-gray-800 font-semibold rounded-md hover:bg-gray-400 transition duration-300"
                  >
                    Back
                  </button>
                )}
              </div>
              <div className="flex space-x-4">
                {step < steps.filter((s) => !s.hidden).length ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="px-6 py-2 bg-secondary text-white font-semibold rounded-md hover:bg-opacity-90 transition duration-300"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-6 py-2 bg-secondary text-white font-semibold rounded-md hover:bg-opacity-90 disabled:bg-secondary/70 transition duration-300"
                  >
                    {isLoading ? 'Submitting...' : 'Sign Up'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="px-6 py-2 bg-transparent border-2 border-secondary text-secondary font-semibold rounded-md hover:bg-secondary hover:text-white transition duration-300"
                >
                  Sign In
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Signup;